import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { END_POINT } from "config/environment";

export type SocialPlatform = "linkedin" | "facebook" | "x";

export interface SocialProviderAvailability {
  feature_enabled: boolean;
  kill_switch: boolean;
  platforms: {
    linkedin: boolean;
    facebook: boolean;
    x: boolean;
  };
}

export interface RecruiterSocialPreferences {
  auto_post_enabled_by_default: boolean;
  default_platforms: SocialPlatform[];
  providers: SocialProviderAvailability;
}

export interface ListingSocialPost {
  id: string;
  platform: SocialPlatform;
  status: "pending" | "processing" | "posted" | "failed" | "skipped";
  external_url?: string | null;
  external_post_id?: string | null;
  last_error?: string | null;
  posted_at?: string | null;
  attempt_count?: number;
}

export interface ListingSocialPostsResponse {
  listing_id: string;
  slug: string;
  share_url: string;
  social_auto_post: boolean;
  social_platforms: SocialPlatform[];
  social_publish_generation: number;
  posts: ListingSocialPost[];
  providers: SocialProviderAvailability;
}

const authHeaders = (token: string) => ({
  Accept: "application/json",
  Authorization: `Bearer ${token}`,
});

export const fetchSocialPreferences = async (
  token: string,
): Promise<RecruiterSocialPreferences> => {
  const response = await axios.get(
    `${END_POINT.BASE_URL}/v1/recruiter/social-preferences`,
    { headers: authHeaders(token) },
  );
  return response.data.data;
};

export const updateSocialPreferences = async (props: {
  token: string;
  auto_post_enabled_by_default: boolean;
  default_platforms: SocialPlatform[];
}): Promise<RecruiterSocialPreferences> => {
  const response = await axios.put(
    `${END_POINT.BASE_URL}/v1/recruiter/social-preferences`,
    {
      auto_post_enabled_by_default: props.auto_post_enabled_by_default,
      default_platforms: props.default_platforms,
    },
    { headers: authHeaders(props.token) },
  );
  return response.data.data;
};

export const fetchListingSocialPosts = async (
  token: string,
  listingId: string,
): Promise<ListingSocialPostsResponse> => {
  const response = await axios.get(
    `${END_POINT.BASE_URL}/v1/jobs/${listingId}/social-posts`,
    { headers: authHeaders(token) },
  );
  return response.data.data;
};

export const useSocialPreferences = (token?: string) =>
  useQuery(
    ["recruiter-social-preferences"],
    () => fetchSocialPreferences(token as string),
    { enabled: Boolean(token) },
  );

export const useUpdateSocialPreferences = () => {
  const queryClient = useQueryClient();
  return useMutation(updateSocialPreferences, {
    onSuccess: () => {
      queryClient.invalidateQueries(["recruiter-social-preferences"]);
    },
  });
};

export const useListingSocialPosts = (
  token?: string,
  listingId?: string | null,
  enabled = true,
) =>
  useQuery(
    ["listing-social-posts", listingId],
    () => fetchListingSocialPosts(token as string, listingId as string),
    {
      enabled: Boolean(token && listingId && enabled),
      refetchInterval: (data) => {
        const hasPending = data?.posts?.some((post) =>
          ["pending", "processing"].includes(post.status),
        );
        return hasPending ? 3000 : false;
      },
    },
  );
